import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  ChaseSchedulePreview,
  IntakeChase,
  UpdateChaseStatusRequest,
} from '../types/intake-chase-types';
import { baseQueryWithErrorHandling } from './baseQuery';

interface ListIntakeChasesParams {
  status?: string;
  intake_ids?: string;
}

export const intakeChaseApiClient = createApi({
  reducerPath: 'intakeChaseApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['IntakeChase', 'IntakeChases', 'ChaseSchedulePreview'] as const,
  endpoints: (builder) => ({
    getChaseSchedulePreview: builder.query<ChaseSchedulePreview, void>({
      query: () => 'api/intakes/chase/schedule-preview/',
      providesTags: ['ChaseSchedulePreview'],
      keepUnusedDataFor: 3600, // Cache for 1 hour — schedule rarely changes
    }),
    getIntakeChase: builder.query<IntakeChase, string>({
      query: (intakeId) => `api/intakes/${intakeId}/chase/`,
      providesTags: (result, error, intakeId) => [
        { type: 'IntakeChase', id: intakeId },
      ],
      keepUnusedDataFor: 60,
    }),
    startIntakeChase: builder.mutation<
      IntakeChase,
      { intakeId: string; start_day?: number }
    >({
      query: ({ intakeId, start_day }) => ({
        url: `api/intakes/${intakeId}/chase/start/`,
        method: 'POST',
        body: start_day && start_day > 1 ? { start_day } : undefined,
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'IntakeChase', id: intakeId },
        'IntakeChases',
      ],
    }),
    updateIntakeChaseStatus: builder.mutation<
      IntakeChase,
      { intakeId: string; body: UpdateChaseStatusRequest }
    >({
      query: ({ intakeId, body }) => ({
        url: `api/intakes/${intakeId}/chase/update-status/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'IntakeChase', id: intakeId },
        'IntakeChases',
      ],
    }),
    listIntakeChases: builder.query<IntakeChase[], ListIntakeChasesParams>({
      query: (params) => ({
        url: 'api/intakes/chases/',
        params,
      }),
      providesTags: ['IntakeChases'],
    }),
  }),
});

export const {
  useGetChaseSchedulePreviewQuery,
  useGetIntakeChaseQuery,
  useStartIntakeChaseMutation,
  useUpdateIntakeChaseStatusMutation,
  useListIntakeChasesQuery,
} = intakeChaseApiClient;
